import { CheckCircle, XCircle } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { Dialog, DialogFooter, DialogHeader, DialogTitle, ScrollableDialogContent } from "@/components/ui/dialog";

type BulkInviteResult = {
  creatorUsername: string;
  success: boolean;
  reason?: string;
};

type BulkInviteResultsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  results: BulkInviteResult[];
  /** True when this call published a draft as a side-effect (real id was just issued by TikTok). */
  wasDraftPublished: boolean;
};

export const BulkInviteResultsDialog = ({
  open,
  onOpenChange,
  results,
  wasDraftPublished,
}: BulkInviteResultsDialogProps) => {
  const succeeded = results.filter((r) => r.success);
  const failed = results.filter((r) => !r.success);
  const total = results.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <ScrollableDialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Invitation summary</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {wasDraftPublished && (
            <div className="rounded-lg border border-utility-green-200 bg-utility-green-50 px-3 py-2 text-sm text-success-primary">
              Draft collaboration was published to TikTok.
            </div>
          )}

          <p className="text-sm text-secondary">
            Invited <span className="font-semibold text-primary">{succeeded.length}</span> of{" "}
            <span className="font-semibold text-primary">{total}</span> creators
            {failed.length > 0 && (
              <>
                {" "}
                · <span className="text-fg-error-primary">{failed.length} failed</span>
              </>
            )}
            .
          </p>

          {failed.length > 0 && (
            <div className="max-h-60 overflow-y-auto overflow-x-hidden rounded-lg border border-secondary px-3 py-2 space-y-2">
              {failed.map((r) => (
                <div key={r.creatorUsername} className="flex items-start gap-2 text-sm min-w-0">
                  <XCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <span className="text-primary font-medium break-words">@{r.creatorUsername}</span>
                    <p className="text-tertiary break-words mt-0.5">{r.reason ?? "Failed to invite"}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {succeeded.length > 0 && failed.length > 0 && (
            <details className="text-sm">
              <summary className="cursor-pointer text-tertiary hover:text-secondary">
                Show {succeeded.length} successful invitation{succeeded.length === 1 ? "" : "s"}
              </summary>
              <div className="mt-2 max-h-40 overflow-y-auto overflow-x-hidden rounded-lg border border-secondary px-3 py-2 space-y-2">
                {succeeded.map((r) => (
                  <div key={r.creatorUsername} className="flex items-start gap-2 text-sm min-w-0">
                    <CheckCircle className="w-4 h-4 text-success-primary shrink-0 mt-0.5" />
                    <span className="text-primary break-words">@{r.creatorUsername}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>

        <DialogFooter>
          <Button type="button" color="primary" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </ScrollableDialogContent>
    </Dialog>
  );
};
